/* Supply › Reports › Sales › Sale Report (Datewise)
 * Item-level view of every sale line billed in a date window — customer, batch,
 * tax, discount and margin, plus the lot's purchase origin so a sale traces back
 * to the GRN that stocked it. Async CSV generation (task → poll → download) in
 * the legacy "Sales Report Template" layout.
 * Data: /pharmacy-ims/api/v1/reports/sales.
 *
 * The bill date window may span at most 45 days (backend rule); the screen opens
 * on the most recent 45 days and blocks a wider range before it fires. */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { useTaskCenter } from "../../Layout/TaskCenterContext";
import supplyService from "../../../Services/supplyService";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import { fmtDate, fmtTime as fmtClock } from "../../../Utils/dateFormat";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconDownload,
} from "../supplyKit";
import { ReportBack } from "./reportShared";
import salesReportService, { PAYMENT_MODES } from "./salesReportService";
import { defaultRange, rangeError } from "./reportApiShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const STATUSES = ["INITIATED", "ISSUED", "CANCELLED"];

/* Same colour semantics Billing.jsx uses for the sales lifecycle, so a status
   reads the same here as it does on the bill itself. The report shows the raw
   status name rather than Billing's operator-facing label, since that's what the
   CSV and the API carry. */
const statusVariant = (status) => {
  if (status === "ISSUED") return "green";
  if (status === "CANCELLED") return "red";
  return "amber";
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const POLL_INTERVAL = 1500;
const POLL_MAX_ATTEMPTS = 40; // ~60s before timing out

/* No warehouse here: the report is scoped to the store selected app-wide in the
   topbar, for every role. A head switches store there and this refetches. */
const defaultFilters = () => ({
  status: "ALL",
  paymentMode: "ALL",
  orderId: "",
  medicineCode: "",
  batchNo: "",
  search: "",
  ...defaultRange(),
});

/* "2026-08-03T09:06:26.715583" -> "09:06:26 AM" — the date is its own column. */
const fmtTime = (iso) => fmtClock(iso);

/* "2029-12-31" -> "12/2029" — a batch expiry only ever means a month. */
const fmtExpiry = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

const pct = (v) => `${(Number(v) || 0).toFixed(2)}%`;

const SaleReportDatewise = () => {
  const { runTask } = useTaskCenter();
  const [filters, setFilters] = useState(defaultFilters);
  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  /* The scope chosen once in the topbar: one store, or every store. "All stores"
     sends no warehouseId, which this endpoint reads as the whole estate — so it is
     a valid scope here, unlike on the stock screens. Only a head who has picked
     nothing at all blocks the fetch. */
  const { code: scopedWh, isAll } = useWarehouse();
  const scopeBlocked = !scopedWh && !isAll;

  /* Debounced copy of `filters` that actually drives the fetch — the code /
     batch / order / search inputs would otherwise fetch per keystroke. */
  const [applied, setApplied] = useState(filters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pageState, setPageState] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const setPageField = (key, val) =>
    setPageState((s) => ({ ...s, [key]: val, ...(key === "size" ? { page: 0 } : {}) }));
  const [submitting, setSubmitting] = useState(false);

  /* Client-side guard for the 45-day rule so a too-wide range never round-trips
     just to come back as a 400. */
  const windowError = rangeError(filters.fromDate, filters.toDate);

  useEffect(() => {
    if (windowError) return undefined;
    const t = setTimeout(() => setApplied(filters), 400);
    return () => clearTimeout(t);
  }, [filters, windowError]);

  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [applied]);

  /* The single place warehouse scope is decided: the store selected app-wide,
     for every role. Forced on here rather than kept in filter state so no code
     path can send a query without it. */
  const scoped = (f) => ({ ...f, warehouseId: scopedWh || undefined });

  useEffect(() => {
    if (scopeBlocked) {
      setRows([]);
      setLoading(false);
      setError("Your warehouse could not be determined, so this report can't be scoped. Please sign in again.");
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    salesReportService
      .list(scoped(applied), { page: pageState.page, size: pageState.size })
      .then((res) => {
        if (!alive) return;
        setRows(res.content || []);
        setPageState((s) => ({ ...s, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "Could not load the sales report.";
        setError(msg);
        toast.error(msg);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, pageState.page, pageState.size, reloadKey, scopedWh, isAll, scopeBlocked]);

  /* Page-scoped roll-ups. The full-window totals live in the CSV — the API pages
     line items, so anything summed here covers only the rows on screen.
     Cancelled bills still hold their line values, so they are summed as billed;
     filter by status to exclude them. */
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.units += r.quantityUnits;
          acc.mrpTotal += r.mrpTotal;
          acc.discount += r.totalDiscountAmount;
          acc.amount += r.amount;
          acc.baseValue += r.baseValue;
          acc.tax += r.taxAmount;
          acc.cost += r.costOfSale;
          acc.margin += r.margin;
          return acc;
        },
        { units: 0, mrpTotal: 0, discount: 0, amount: 0, baseValue: 0, tax: 0, cost: 0, margin: 0 }
      ),
    [rows]
  );
  const marginPct = totals.baseValue ? (totals.margin / totals.baseValue) * 100 : 0;

  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);

  /* Kick off the async CSV via the global task center so it keeps running (and
     shows progress in the topbar bell) even if the user navigates away. */
  const startExport = () => {
    if (submitting || windowError || scopeBlocked) return;
    setSubmitting(true);
    const exportFilters = scoped(applied);
    runTask({
      title: "Sale Report (Datewise) · CSV",
      run: async (onProgress) => {
        onProgress("Requesting…");
        const task = await salesReportService.requestExport(exportFilters);
        if (!task?.taskId) throw new Error("Export did not return a task id.");
        const fileName = task.fileName || `sales-report-${exportFilters.warehouseId || "all"}.csv`;
        const doDownload = () => salesReportService.download(task.taskId, fileName);

        let cur = task;
        for (let i = 0; cur.status === "PENDING" && i < POLL_MAX_ATTEMPTS; i++) {
          onProgress("Generating report…");
          await sleep(POLL_INTERVAL);
          cur = await salesReportService.exportStatus(task.taskId);
        }
        if (cur.status === "FAILED") throw new Error(cur.message || "Report generation failed.");
        if (cur.status !== "READY") throw new Error("Report generation timed out. Please try again.");

        onProgress("Downloading…");
        await doDownload();
        return {
          detail: `Downloaded${cur.rowCount != null ? ` · ${cbFmtNum(cur.rowCount)} rows` : ""}`,
          action: { label: "Download again", run: doDownload },
        };
      },
    });
    toast.info("Generating report — track progress in notifications.");
    // brief guard against double-submit; the task itself lives in the task center
    setTimeout(() => setSubmitting(false), 1200);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.SALES_REPORT} label="Sales Reports" />
      <SectionHeader
        title="Sale Report (Datewise)"
        sub="Every sale line billed in the window — batch, tax and margin · generate a CSV to download"
        actions={
          <Button
            variant="primary"
            icon={<IconDownload size={14} />}
            onClick={startExport}
            disabled={submitting || !!windowError || scopeBlocked}
          >
            Generate CSV
          </Button>
        }
      />

      <ToolbarBar>
        <DateRangeFilter
          label="Bill date"
          from={filters.fromDate}
          to={filters.toDate}
          onFromChange={(v) => setF("fromDate", v)}
          onToChange={(v) => setF("toDate", v)}
        />
        <Input
          value={filters.medicineCode}
          onChange={(e) => setF("medicineCode", e.target.value)}
          placeholder="Product code…"
          mono
          style={{ width: 140, maxWidth: "100%" }}
        />
        <Input
          value={filters.batchNo}
          onChange={(e) => setF("batchNo", e.target.value)}
          placeholder="Batch no…"
          mono
          style={{ width: 130, maxWidth: "100%" }}
        />
        <Input
          value={filters.orderId}
          onChange={(e) => setF("orderId", e.target.value)}
          placeholder="Order ID…"
          mono
          style={{ width: 140, maxWidth: "100%" }}
        />
        <Input
          value={filters.search}
          onChange={(e) => setF("search", e.target.value)}
          placeholder="Bill no, customer or product…"
          style={{ width: 220, maxWidth: "100%" }}
        />
        <Button onClick={() => setFilters(defaultFilters())}>Reset</Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} sale lines
        </div>
      </ToolbarBar>

      {windowError && <InlineAlert tone="amber">{windowError}</InlineAlert>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Sale lines" value={cbFmtNum(pageState.totalElements)} variant="ink" />
        <KpiBox label="Units sold (this page)" value={cbFmtNum(totals.units)} variant="ink" />
        <KpiBox
          label="Sale value (this page)"
          value={cbFmtINR(totals.amount)}
          sub={`${cbFmtINR(totals.baseValue)} net + ${cbFmtINR(totals.tax)} GST`}
          variant="green"
        />
        <KpiBox
          label="Margin (this page)"
          value={cbFmtINR(totals.margin)}
          sub={`${pct(marginPct)} of net sales · cost ${cbFmtINR(totals.cost)}`}
          variant="amber"
        />
      </div>

      {loading ? (
        <LoadingState label="Loading sales report…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            sticky
            columns={[
              { label: "Bill date" },
              { label: "Time" },
              { label: "Bill no" },
              { label: "Store" },
              { label: "Customer" },
              { label: "Product" },
              { label: "Batch" },
              { label: "Exp" },
              { label: "Qty", align: "right" },
              { label: "UOM" },
              { label: "MRP", align: "right" },
              { label: "Rate", align: "right" },
              { label: "MRP total", align: "right" },
              { label: "Disc", align: "right" },
              { label: "Amount", align: "right" },
              { label: "Net sales", align: "right" },
              { label: "Tax", align: "right" },
              { label: "GST", align: "right" },
              { label: "Cost", align: "right" },
              { label: "Margin", align: "right" },
              { label: "Margin %", align: "right" },
              { label: "Pay mode" },
              { label: "Billed by" },
              { label: "Status" },
            ]}
          >
            {rows.length === 0 && (
              <tr>
                <Td colSpan={24} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                  No sale lines match these filters.
                </Td>
              </tr>
            )}
            {rows.map((r, i) => (
              <Tr key={`${r.invoiceId}-${r.medicineCode}-${r.batchNo}-${i}`}>
                <Td mono style={{ fontWeight: 600 }}>
                  {fmtDate(r.invoiceDate)}
                </Td>
                <Td mono style={{ color: "var(--fg-muted)" }}>
                  {fmtTime(r.createdAt)}
                </Td>
                <Td mono title={r.orderId ? `Order ${r.orderId}` : undefined}>
                  {r.invoiceNumber}
                </Td>
                {/* warehouseId is the pharmacy code the rest of the app keys on
                    (CBP826); warehouseCode/name are the store's own labels. */}
                <Td title={[r.warehouseName, r.city, r.state].filter(Boolean).join(" · ")}>
                  <Pill variant="neutral">{r.warehouseId || r.warehouseCode}</Pill>
                </Td>
                <Td
                  style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}
                  title={[r.customerName, r.customerPhone, r.doctorName && `Dr: ${r.doctorName}`]
                    .filter(Boolean)
                    .join(" · ")}
                >
                  {r.customerName || <span style={{ color: "var(--fg-muted)" }}>—</span>}
                </Td>
                <Td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }} title={r.medicineName}>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--fg-muted)", marginRight: 6 }}>
                    {r.medicineCode}
                  </span>
                  {r.medicineName}
                </Td>
                <Td
                  mono
                  title={
                    r.batchSupplierName
                      ? `From ${r.batchSupplierName} · ${r.batchInvoiceNumber || "—"} · ${r.batchInvoiceDate || "—"}`
                      : undefined
                  }
                >
                  {r.batchNo}
                </Td>
                <Td mono style={{ color: "var(--fg-muted)" }}>
                  {fmtExpiry(r.expiryDate)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }}>
                  {cbFmtNum(r.quantityUnits)}
                </Td>
                <Td style={{ color: "var(--fg-muted)", fontSize: 11 }}>{r.unitOfMeasure}</Td>
                <Td mono right>
                  {cbFmtINR(r.mrp)}
                </Td>
                <Td mono right>
                  {cbFmtINR(r.ratePerStrip)}
                </Td>
                <Td mono right>
                  {cbFmtINR(r.mrpTotal)}
                </Td>
                <Td
                  mono
                  right
                  style={{ color: "var(--fg-muted)" }}
                  title={`${pct(r.totalDiscountPercentage)} of MRP${
                    r.cureCoinDiscount ? ` · incl. ${cbFmtINR(r.cureCoinDiscount)} CureCoin` : ""
                  }`}
                >
                  {cbFmtINR(r.totalDiscountAmount)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }}>
                  {cbFmtINR(r.amount)}
                </Td>
                <Td mono right>
                  {cbFmtINR(r.baseValue)}
                </Td>
                <Td mono right style={{ color: "var(--fg-muted)" }}>
                  {pct(r.gstPercentage)}
                </Td>
                <Td
                  mono
                  right
                  title={`CGST ${cbFmtINR(r.cgstAmount)} · SGST ${cbFmtINR(r.sgstAmount)} · IGST ${cbFmtINR(
                    r.igstAmount
                  )}`}
                >
                  {cbFmtINR(r.taxAmount)}
                </Td>
                <Td mono right style={{ color: "var(--fg-muted)" }}>
                  {cbFmtINR(r.costOfSale)}
                </Td>
                <Td mono right style={{ color: r.margin < 0 ? "var(--red-700)" : "var(--green-700)" }}>
                  {cbFmtINR(r.margin)}
                </Td>
                <Td mono right style={{ color: r.margin < 0 ? "var(--red-700)" : "var(--fg-muted)" }}>
                  {pct(r.marginPercentage)}
                </Td>
                <Td>
                  <Pill variant="neutral">{(r.paymentMode || "—").replace(/_/g, " ")}</Pill>
                </Td>
                <Td style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis" }} title={r.createdBy}>
                  {r.createdBy || <span style={{ color: "var(--fg-muted)" }}>—</span>}
                </Td>
                <Td>
                  <Pill variant={statusVariant(r.status)} dot>
                    {r.status}
                  </Pill>
                </Td>
              </Tr>
            ))}
          </Table>

          <ToolbarBar>
            <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {start}–{end} of {cbFmtNum(pageState.totalElements)}
              </span>
              <Button
                size="sm"
                disabled={pageState.page <= 0}
                onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={pageState.page + 1 >= pageState.totalPages}
                onClick={() => setPageField("page", pageState.page + 1)}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}
    </div>
  );
};

export default SaleReportDatewise;
